import { uniqBy } from 'lodash'
import { useCallback, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import {
  getUserBundleItems,
  transformUserBundleItemsResponse,
  BundleSummaryModel,
} from '@marketplace-web/bundles/items-bundle-data'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'

const PER_PAGE = 20
const DEFAULT_PAGE = 0

type Props = {
  preselectedItemIds: Array<number>
  userId: number
  bundleSummary?: BundleSummaryModel
}

const useUserBundleItems = ({ preselectedItemIds, userId, bundleSummary }: Props) => {
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE)
  const [endReached, setEndReached] = useState(false)
  const [items, setItems] = useState<Array<ProductItemModel>>([])

  const { fetch, isLoading } = useFetch(getUserBundleItems, transformUserBundleItemsResponse)

  const fetchNextPage = useCallback(async () => {
    if (!userId || endReached) return

    const { transformedData } = await fetch({
      userId,
      preselectedItemIds,
      perPage: PER_PAGE,
      currentPage,
    })

    if (!transformedData) return

    const { currentPage: newCurrentPage, totalPages } = transformedData.pagination

    setItems(prevItems => uniqBy([...prevItems, ...transformedData.items], 'id'))
    setCurrentPage(newCurrentPage)
    setEndReached(newCurrentPage === totalPages)
  }, [currentPage, endReached, fetch, preselectedItemIds, userId])

  return {
    items,
    isLoading,
    fetchNextPage,
    endReached,
    offlineVerificationFee: bundleSummary?.offlineVerificationFee || null,
    containsOfflineVerificationItem: bundleSummary?.containsOfflineVerificationItem || false,
  }
}

export default useUserBundleItems
